import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Button, IconButton, Chip, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, LinearProgress, Tooltip, Divider, useTheme, alpha,
  CircularProgress, InputAdornment,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StorageIcon from '@mui/icons-material/Storage';
import ReplayIcon from '@mui/icons-material/Replay';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';
import ReportBuilderDialog from './reportsScheduler/ReportBuilderDialog';
import ParameterDefaultsDialog from './reportsScheduler/ParameterDefaultsDialog';
import { NotificationContext } from '../context/NotificationContext';
import { API_ENDPOINTS } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS } from '../constants/colors';

const ACCENT = TOOL_COLORS.OTHER.primary;

// Runs poll while anything is active. A scheduled report can take minutes, so
// the interval is deliberately slow — this competes with report execution on a
// 4 GB box.
const POLL_MS = 4000;

const STATUS_COLOR = {
  completed: 'success', running: 'info', queued: 'default',
  failed: 'error', cancelled: 'warning',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwtToken')}` });

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const fmtDuration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

// "copiesOk" -> "Copies ok", "webIdsKept" -> "Web ids kept"
const label = (k) => k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const isTrue = (v) => v === true || String(v ?? '').trim().toLowerCase() === 'true';

// A report parameter as a form field: Bool -> switch, everything else -> text.
// Values travel as strings (the API parses them), so a switch writes 'true'/'false'.
const ParamField = ({ p, value, onChange }) => {
  if (p.type === 'Bool') {
    return (
      <FormControlLabel
        key={p.name}
        sx={{ display: 'flex', mb: 1 }}
        control={<Switch checked={isTrue(value ?? p.default)} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />}
        label={(
          <Box>
            <Typography variant="body2">{label(p.name)}</Typography>
            {p.description && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.description}</Typography>}
          </Box>
        )}
      />
    );
  }
  return (
    <TextField
      key={p.name}
      label={`${p.name}${p.required ? ' *' : ''}`}
      helperText={p.description + (p.default != null && p.default !== '' ? ` (default ${p.default})` : '')}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="small" fullWidth sx={{ mb: 2 }}
      type={p.type === 'Date' ? 'date' : 'text'}
      InputLabelProps={p.type === 'Date' ? { shrink: true } : undefined}
    />
  );
};

const Kv = ({ rows }) => (
  <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>{k}</Typography>
        <Typography component="dd" variant="caption" sx={{ m: 0, fontFamily: 'monospace', wordBreak: 'break-word' }}>{v}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

const ReportsScheduler = () => {
  const theme = useTheme();
  const { showNotification } = useContext(NotificationContext);

  const [tab, setTab] = useState(0);
  const [reports, setReports] = useState([]);
  const [reportSearch, setReportSearch] = useState('');
  const [runs, setRuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [health, setHealth] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [seesAll, setSeesAll] = useState(false);
  const [customReports, setCustomReports] = useState([]);
  const [builder, setBuilder] = useState(null);         // { report } | null - the report being built or edited
  const [paramsFor, setParamsFor] = useState(null);     // report key whose defaults are being edited

  const [email, setEmail] = useState(null);             // { settings, credentials, ... } from the API
  const [emailForm, setEmailForm] = useState(null);     // the editable copy
  const [emailBusy, setEmailBusy] = useState(false);
  const [testTo, setTestTo] = useState('');

  const [runDialog, setRunDialog] = useState(null);      // { report, params }
  const [runDetail, setRunDetail] = useState(null);      // full run record with log
  const [schedDialog, setSchedDialog] = useState(null);  // { schedule|null, form }
  const [cronPreview, setCronPreview] = useState([]);

  const pollRef = useRef(null);
  const detailRef = useRef(null);

  // ── loading ────────────────────────────────────────────────────────────────
  const get = useCallback(async (url) => {
    const r = await fetch(url, { headers: authHeaders() });
    if (r.status === 403) throw new Error('Reports Scheduler is limited to Administrator and Owner accounts');
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const d = await get(`${API_ENDPOINTS.REPORTS_SCHEDULER_RUNS}?limit=60`);
      setRuns(d.runs || []);
      // "all" means the caller is an Administrator or Owner and is looking at
      // everybody's work; "mine" means the list is already theirs alone.
      setSeesAll(d.scope === 'all');
      return d.runs || [];
    } catch (e) { return []; }
  }, [get]);

  const loadAll = useCallback(async () => {
    setError('');
    try {
      const [rep, sch, tz] = await Promise.all([
        get(API_ENDPOINTS.REPORTS_SCHEDULER_REPORTS),
        get(API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULES),
        get(API_ENDPOINTS.REPORTS_SCHEDULER_TIMEZONES),
      ]);
      setReports(rep.reports || []);
      setSchedules(sch.schedules || sch || []);
      setTimezones(tz.timeZones || tz || []);
      await loadRuns();
      try { setHealth(await get(API_ENDPOINTS.REPORTS_SCHEDULER_HEALTH)); } catch { setHealth(null); }
      try { setStore(await get(API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULES_STORE)); } catch { setStore(null); }
      try {
        const cr = await get(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_REPORTS);
        setCustomReports(cr.reports || []);
      } catch { setCustomReports([]); }
      try {
        const em = await get(API_ENDPOINTS.REPORTS_SCHEDULER_EMAIL);
        setEmail(em);
        setEmailForm((prev) => prev || { ...em.settings });
      } catch { setEmail(null); }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [get, loadRuns]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll only while something is actually running.
  useEffect(() => {
    const anyActive = runs.some((r) => r.status === 'running' || r.status === 'queued');
    if (!anyActive) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (!pollRef.current) pollRef.current = setInterval(loadRuns, POLL_MS);
    return undefined;
  }, [runs, loadRuns]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // The open run's detail (log tail, summary) refreshes while it is active.
  const loadRunDetail = useCallback(async (id) => {
    try {
      const d = await get(`${API_ENDPOINTS.REPORTS_SCHEDULER_RUN(id)}?log=150`);
      setRunDetail(d);
      return d;
    } catch (e) { showNotification(e.message, 'error'); return null; }
  }, [get, showNotification]);

  useEffect(() => {
    if (!runDetail || !(runDetail.status === 'running' || runDetail.status === 'queued')) {
      if (detailRef.current) { clearInterval(detailRef.current); detailRef.current = null; }
      return undefined;
    }
    if (!detailRef.current) detailRef.current = setInterval(() => loadRunDetail(runDetail.id), POLL_MS);
    return undefined;
  }, [runDetail, loadRunDetail]);

  useEffect(() => () => { if (detailRef.current) clearInterval(detailRef.current); }, []);

  // ── actions ────────────────────────────────────────────────────────────────
  const startRun = async (reportKey, parameters) => {
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_RUNS, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportKey, parameters }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      showNotification('Report started', 'success');
      setRunDialog(null);
      setTab(1);
      loadRuns();
      return d;
    } catch (e) { showNotification(`Could not start: ${e.message}`, 'error'); return null; }
  };

  const cancelOrDelete = async (id) => {
    try {
      await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_RUN(id), { method: 'DELETE', headers: authHeaders() });
      if (runDetail && runDetail.id === id) setRunDetail(null);
      loadRuns();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  const download = async (run) => {
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_RUN_DOWNLOAD(run.id), { headers: authHeaders() });
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = run.fileName || `${run.reportKey}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (e) { showNotification(`Download failed: ${e.message}`, 'error'); }
  };

  const saveSchedule = async () => {
    const { schedule, form } = schedDialog;
    const body = {
      name: form.name, reportKey: form.reportKey, cron: form.cron,
      timeZone: form.timeZone, enabled: form.enabled, parameters: form.parameters,
      emailEnabled: form.emailEnabled !== false,
      emailTo: form.emailTo || '',
      emailCc: form.emailCc || '',
      emailRecipientsReplaceDefault: !!form.emailRecipientsReplaceDefault,
      emailAttachFile: form.emailAttachFile !== false,
    };
    try {
      const url = schedule
        ? API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULE(schedule.id)
        : API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULES;
      const r = await fetch(url, {
        method: schedule ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      showNotification(schedule ? 'Schedule updated' : 'Schedule created', 'success');
      setSchedDialog(null);
      loadAll();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  const toggleSchedule = async (s, enabled) => {
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULE(s.id), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...s, enabled }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      showNotification(enabled ? 'Schedule on' : 'Schedule paused', 'success');
      loadAll();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  const deleteSchedule = async (id) => {
    try {
      await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULE(id), { method: 'DELETE', headers: authHeaders() });
      loadAll();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  const runScheduleNow = async (id) => {
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_SCHEDULE_RUN(id), { method: 'POST', headers: authHeaders() });
      if (!r.ok) throw new Error(`${r.status}`);
      showNotification('Started', 'success');
      setTab(1);
      loadRuns();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  // Ask the server what the cron means rather than parsing it here — it owns
  // the expression format and the time-zone rules.
  const previewCron = useCallback(async (cron, tz) => {
    if (!cron) { setCronPreview([]); return; }
    try {
      const d = await get(`${API_ENDPOINTS.REPORTS_SCHEDULER_CRON_PREVIEW}?cron=${encodeURIComponent(cron)}&tz=${encodeURIComponent(tz)}`);
      setCronPreview(d.occurrences || d || []);
    } catch { setCronPreview([]); }
  }, [get]);

  useEffect(() => {
    if (!schedDialog) return;
    const t = setTimeout(() => previewCron(schedDialog.form.cron, schedDialog.form.timeZone), 350);
    return () => clearTimeout(t);
  }, [schedDialog, previewCron]);

  // ── render helpers ─────────────────────────────────────────────────────────
  const openRunDialog = (report) => {
    const params = {};
    (report.parameters || []).forEach((p) => { params[p.name] = p.default ?? ''; });
    setRunDialog({ report, params });
  };

  const deleteCustomReport = async (key, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${name}"? Any schedule pointing at it will stop working.`)) return;
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_REPORT(key), {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `${r.status}`);
      }
      showNotification('Report deleted', 'success');
      loadAll();
    } catch (e) { showNotification(e.message, 'error'); }
  };

  const saveEmail = async () => {
    setEmailBusy(true);
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_EMAIL, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setEmail((prev) => ({ ...(prev || {}), settings: d.settings, credentials: d.credentials }));
      setEmailForm({ ...d.settings });
      showNotification(d.warning || 'Email settings saved', d.warning ? 'warning' : 'success');
    } catch (e) { showNotification(e.message, 'error'); } finally { setEmailBusy(false); }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_EMAIL_TEST, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testTo }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      showNotification(`Test sent - ${d.detail}`, 'success');
    } catch (e) { showNotification(`Test failed: ${e.message}`, 'error'); } finally { setEmailBusy(false); }
  };

  // Re-send the notification for a run whose mail went nowhere - usually
  // because the recipients were wrong when it finished.
  const emailRun = async (id) => {
    try {
      const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_EMAIL_RUN(id), { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      showNotification(`Sent - ${d.detail}`, 'success');
    } catch (e) { showNotification(`Not sent: ${e.message}`, 'error'); }
  };

  const openScheduleDialog = (schedule) => {
    const first = reports[0];
    setSchedDialog({
      schedule,
      form: schedule
        ? { ...schedule, parameters: { ...(schedule.parameters || {}) } }
        : {
            name: '', reportKey: first ? first.key : '', cron: '30 6 * * 1-5',
            timeZone: 'America/New_York', enabled: true, parameters: {},
            emailEnabled: true, emailTo: '', emailCc: '',
            emailRecipientsReplaceDefault: false, emailAttachFile: true,
          },
    });
  };

  const dialogReport = schedDialog
    ? reports.find((r) => r.key === schedDialog.form.reportKey)
    : null;

  const customByKey = {};
  customReports.forEach((r) => { customByKey[r.key] = r; });

  const emailOn = !!(email && email.settings && email.settings.enabled);
  const emailReady = !!(email && email.credentials && email.credentials.configured);

  // Reports filtered by the search box (name, description, key, category, parameter
  // names — every word typed must match somewhere), then grouped by category in the
  // order the API returns them.
  const terms = reportSearch.toLowerCase().split(/\s+/).filter(Boolean);
  const visibleReports = terms.length === 0 ? reports : reports.filter((r) => {
    const hay = [r.name, r.description, r.key, r.category, ...(r.parameters || []).map((p) => p.name)]
      .join(' ').toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
  const categories = [];
  visibleReports.forEach((r) => { if (!categories.includes(r.category)) categories.push(r.category); });

  const storeName = store ? String(store.store || store.description || '') : '';
  const storeOk = store && store.loaded !== false && !store.error;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Reports Scheduler" category="other" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary, textAlign: 'center', fontWeight: 600,
            letterSpacing: '0.5px', borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2, mb: 1,
          }}
        >
          REPORTS SCHEDULER
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary, mb: 2 }}>
          Run a report now, or put it on a timetable — Administrator and Owner only
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small" variant="outlined"
            color={health && health.reachable ? 'success' : 'warning'}
            label={health && health.reachable ? 'Warehouse connected' : (health && health.configured ? 'Warehouse unreachable' : 'Warehouse not configured')}
          />
          {store && (
            <Tooltip title={storeName}>
              <Chip
                size="small" variant="outlined" icon={<StorageIcon />}
                color={storeOk ? 'success' : 'warning'}
                label={storeOk ? `Schedules in ${storeName.split(' ')[0] || 'store'}` : 'Schedule store not answering'}
              />
            </Tooltip>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {health && health.reachable === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warehouse not reachable</AlertTitle>
            Reports that query the warehouse will fail until this is restored.
          </Alert>
        )}
        {store && !storeOk && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Schedule store not answering</AlertTitle>
            {store.error || 'Schedules could not be loaded from the database; timetables will not fire until it is back.'}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab label={terms.length ? `Reports (${visibleReports.length} of ${reports.length})` : `Reports (${reports.length})`} />
              <Tab label={`Runs (${runs.length})`} />
              <Tab label={`Schedules (${schedules.length})`} />
              <Tab label="Email" icon={emailOn ? <MailOutlineIcon fontSize="small" /> : undefined} iconPosition="end" />
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {tab === 0 && (
                <TextField
                  size="small"
                  placeholder="Search reports…"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setReportSearch(''); }}
                  autoComplete="off"
                  sx={{ width: { xs: 180, sm: 280 } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    endAdornment: reportSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setReportSearch('')} aria-label="clear search"><ClearIcon fontSize="small" /></IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
              {tab === 0 && seesAll && (
                <Button
                  size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => setBuilder({ report: null })}
                  sx={{ whiteSpace: 'nowrap', bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
                >
                  New report
                </Button>
              )}
              <Tooltip title="Refresh">
                <IconButton onClick={loadAll} size="small"><RefreshIcon /></IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider />

          {/* ── Reports ── */}
          {tab === 0 && (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {['Report', 'Typical time', 'Parameters', ''].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {visibleReports.length === 0 && (
                    <TableRow><TableCell colSpan={4}>
                      <Typography variant="body2" sx={{ p: 2, color: theme.palette.text.secondary }}>
                        No reports match “{reportSearch}”.
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {categories.map((cat) => (
                    <React.Fragment key={cat}>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ bgcolor: alpha(ACCENT, 0.06), py: 0.5 }}>
                          <Typography variant="overline" sx={{ fontWeight: 600 }}>{cat}</Typography>
                        </TableCell>
                      </TableRow>
                      {visibleReports.filter((r) => r.category === cat).map((r) => (
                        <TableRow key={r.key} hover>
                          <TableCell sx={{ maxWidth: 620 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.name}
                              {customByKey[r.key] && (
                                <Chip size="small" variant="outlined" label="built here" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {r.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={r.duration === 'minutes' ? 'warning' : 'default'}
                              variant="outlined"
                              label={r.duration}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {(r.parameters || []).map((p) => p.name).join(', ') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="small" startIcon={<PlayArrowIcon />}
                              onClick={() => openRunDialog(r)}
                              sx={{ color: ACCENT }}
                            >
                              Run
                            </Button>
                            {seesAll && (r.parameters || []).length > 0 && (
                              <Tooltip title="Change the values this report starts from">
                                <Button
                                  size="small" startIcon={<TuneIcon />}
                                  onClick={() => setParamsFor(r.key)}
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  Parameters
                                </Button>
                              </Tooltip>
                            )}
                            {seesAll && customByKey[r.key] && (
                              <>
                                <Tooltip title="Edit this report">
                                  <IconButton size="small" onClick={() => setBuilder({ report: customByKey[r.key] })}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete this report">
                                  <IconButton size="small" onClick={() => deleteCustomReport(r.key, r.name)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Runs ── */}
          {tab === 1 && (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {['Report', ...(seesAll ? ['Owner'] : []), 'Status', 'Trigger', 'Started', 'Duration', 'Rows', 'File', ''].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {runs.length === 0 && (
                    <TableRow><TableCell colSpan={seesAll ? 9 : 8}>
                      <Typography variant="body2" sx={{ p: 2, color: theme.palette.text.secondary }}>
                        No runs yet.
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {runs.map((r) => (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => loadRunDetail(r.id)}>
                      <TableCell>
                        <Typography variant="body2">{r.reportName}</Typography>
                        {r.scheduleName && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            via {r.scheduleName}
                          </Typography>
                        )}
                      </TableCell>
                      {seesAll && (
                        <TableCell>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {r.ownerEmail || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip size="small" color={STATUS_COLOR[r.status] || 'default'} label={r.status} />
                        {r.status === 'running' && (
                          <Box sx={{ mt: 0.5, minWidth: 120 }}>
                            <LinearProgress
                              variant={r.total > 0 ? 'determinate' : 'indeterminate'}
                              value={r.total > 0 ? (r.processed / r.total) * 100 : 0}
                            />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {r.step}{r.total > 0 ? ` ${r.processed}/${r.total}` : ''}
                            </Typography>
                          </Box>
                        )}
                        {r.error && (
                          <Tooltip title={r.error}>
                            <Typography variant="caption" sx={{ display: 'block', color: theme.palette.error.main, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.error}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell><Typography variant="caption">{r.trigger}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{fmtWhen(r.startedUtc || r.createdUtc)}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{fmtDuration(r.durationMs)}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{r.rowCount != null ? r.rowCount.toLocaleString() : '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{fmtBytes(r.fileSize)}</Typography></TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {r.download && (
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => download(r)}><DownloadIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={r.status === 'running' || r.status === 'queued' ? 'Cancel' : 'Delete'}>
                          <IconButton size="small" onClick={() => cancelOrDelete(r.id)}>
                            {r.status === 'running' || r.status === 'queued'
                              ? <StopIcon fontSize="small" />
                              : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Schedules ── */}
          {tab === 2 && (
            <Box>
              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained" startIcon={<AddIcon />}
                  onClick={() => openScheduleDialog(null)}
                  sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
                >
                  New schedule
                </Button>
              </Box>
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    {['On', 'Name', 'Report', 'Cron', 'Time zone', 'Next run', 'Last run', ''].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {schedules.length === 0 && (
                      <TableRow><TableCell colSpan={8}>
                        <Typography variant="body2" sx={{ p: 2, color: theme.palette.text.secondary }}>
                          No schedules yet.
                        </Typography>
                      </TableCell></TableRow>
                    )}
                    {schedules.map((s) => (
                      <TableRow key={s.id} hover sx={{ opacity: s.enabled ? 1 : 0.6 }}>
                        <TableCell padding="checkbox">
                          <Switch size="small" checked={!!s.enabled} onChange={(e) => toggleSchedule(s, e.target.checked)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                          {Object.keys(s.parameters || {}).length > 0 && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: 'monospace' }}>
                              {Object.entries(s.parameters).map(([k, v]) => `${k}=${v}`).join(' ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell><Typography variant="caption">{s.reportName || s.reportKey}</Typography></TableCell>
                        <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.cron}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{s.timeZone}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{s.enabled ? fmtWhen(s.nextRunUtc) : 'paused'}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtWhen(s.lastRunUtc)}</Typography>
                          {s.lastRunStatus && (
                            <Chip size="small" sx={{ ml: 0.5 }} color={STATUS_COLOR[s.lastRunStatus] || 'default'} label={s.lastRunStatus} />
                          )}
                          {s.lastEmailStatus && (
                            <Tooltip title={s.lastEmailStatus}>
                              <MailOutlineIcon
                                fontSize="small"
                                sx={{
                                  ml: 0.5, verticalAlign: 'middle', fontSize: 16,
                                  color: s.lastEmailStatus.startsWith('sent')
                                    ? theme.palette.success.main
                                    : theme.palette.text.disabled,
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Run now"><IconButton size="small" onClick={() => runScheduleNow(s.id)}><PlayArrowIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openScheduleDialog(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteSchedule(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {tab === 3 && (
            <Box sx={{ p: 2, maxWidth: 780 }}>
              {!email && (
                <Alert severity="warning">Email settings could not be loaded.</Alert>
              )}

              {email && !emailReady && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Microsoft 365 credentials are not set up</AlertTitle>
                  <Typography variant="caption" component="div">
                    Missing from AWS Secrets Manager: <strong>{(email.credentials.missing || []).join(', ')}</strong>.
                    Until those are in place nothing can be sent, whatever is configured below.
                  </Typography>
                </Alert>
              )}

              {email && emailReady && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="caption" component="div">
                    Sending as <strong>{email.credentials.sender}</strong>, using application credentials
                    held in {email.credentials.source}. The credentials are not readable or editable from
                    this page by design.
                  </Typography>
                </Alert>
              )}

              {emailForm && (
                <>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!!emailForm.enabled}
                        onChange={(e) => setEmailForm({ ...emailForm, enabled: e.target.checked })}
                      />
                    )}
                    label="Send an email when a schedule runs"
                  />

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Default recipients (To)" size="small" fullWidth
                        value={emailForm.defaultTo || ''}
                        placeholder="someone@safavieh.com, someone.else@safavieh.com"
                        helperText="Comma separated. Every schedule uses these unless it replaces them."
                        onChange={(e) => setEmailForm({ ...emailForm, defaultTo: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Default Cc" size="small" fullWidth
                        value={emailForm.defaultCc || ''}
                        onChange={(e) => setEmailForm({ ...emailForm, defaultCc: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>When to send</Typography>
                    <FormControlLabel
                      control={<Switch checked={!!emailForm.notifyOnSuccess} onChange={(e) => setEmailForm({ ...emailForm, notifyOnSuccess: e.target.checked })} />}
                      label="On success"
                    />
                    <FormControlLabel
                      control={<Switch checked={!!emailForm.notifyOnFailure} onChange={(e) => setEmailForm({ ...emailForm, notifyOnFailure: e.target.checked })} />}
                      label="On failure"
                    />
                    <FormControlLabel
                      control={<Switch checked={!!emailForm.includeManualRuns} onChange={(e) => setEmailForm({ ...emailForm, includeManualRuns: e.target.checked })} />}
                      label="Also for runs started by hand"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>The file</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={7}>
                      <FormControlLabel
                        control={<Switch checked={!!emailForm.attachFile} onChange={(e) => setEmailForm({ ...emailForm, attachFile: e.target.checked })} />}
                        label="Attach the report when it is small enough"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Attachment limit (MB)" size="small" type="number" fullWidth
                        value={emailForm.maxAttachmentMb ?? 3}
                        inputProps={{ min: 0, max: email ? email.maxAttachmentMbSupported : 3 }}
                        helperText={`Microsoft 365 caps inline attachments at about ${email ? email.maxAttachmentMbSupported : 3} MB`}
                        onChange={(e) => setEmailForm({ ...emailForm, maxAttachmentMb: Number(e.target.value) })}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Anything larger is linked instead of attached - the mail points at the Runs tab,
                    which is where big files like the Customer Style List zip are downloaded from.
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Subject prefix" size="small" fullWidth
                        value={emailForm.subjectPrefix || ''}
                        onChange={(e) => setEmailForm({ ...emailForm, subjectPrefix: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label="Site address (for the link in the mail)" size="small" fullWidth
                        value={emailForm.siteBaseUrl || ''}
                        onChange={(e) => setEmailForm({ ...emailForm, siteBaseUrl: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Recipients must be at these domains" size="small" fullWidth
                        value={emailForm.allowedRecipientDomains ?? ''}
                        placeholder="safavieh.com"
                        helperText={'Comma separated. Anyone who can create a schedule can choose who its output '
                          + 'is mailed to, so this bounds where report data can go. Leave blank for no limit. '
                          + 'It does not restrict the default recipients above.'}
                        onChange={(e) => setEmailForm({ ...emailForm, allowedRecipientDomains: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      variant="contained" onClick={saveEmail} disabled={emailBusy}
                      sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
                    >
                      Save
                    </Button>
                    {email && email.settings && email.settings.updatedUtc && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Last changed {fmtWhen(email.settings.updatedUtc)}
                        {email.settings.updatedBy ? ` by ${email.settings.updatedBy}` : ''}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Send a test</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small" fullWidth label="To"
                      placeholder={emailForm.defaultTo || 'someone@safavieh.com'}
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      helperText="Leave blank to use the default recipients. Nothing is attached."
                    />
                    <Button
                      variant="outlined" startIcon={<SendIcon />} onClick={sendTestEmail}
                      disabled={emailBusy || !emailReady} sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
                    >
                      Send test
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>

        <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ mt: 2 }}>
          <Typography variant="caption" component="div">
            Schedules are stored in the website database and survive deployments. Run history and
            output files live on the API instance under <strong>REPORTS_DATA_DIR</strong>; Elastic
            Beanstalk replaces the app directory on every deploy, so point that somewhere outside it
            if history needs to survive. Runs keep going if you leave the page.
          </Typography>
        </Alert>
      </Box>

      {/* ── Run dialog ── */}
      <Dialog open={!!runDialog} onClose={() => setRunDialog(null)} maxWidth="sm" fullWidth>
        {runDialog && (
          <>
            <DialogTitle>Run {runDialog.report.name}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                {runDialog.report.description}
              </Typography>
              {(runDialog.report.parameters || []).map((p) => (
                <ParamField
                  key={p.name} p={p} value={runDialog.params[p.name]}
                  onChange={(v) => setRunDialog({ ...runDialog, params: { ...runDialog.params, [p.name]: v } })}
                />
              ))}
              {(runDialog.report.parameters || []).length === 0 && (
                <Typography variant="body2">This report takes no parameters.</Typography>
              )}
              {runDialog.report.duration === 'minutes' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    This one runs for minutes, not seconds. It keeps going if you leave the page —
                    watch it on the Runs tab.
                  </Typography>
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRunDialog(null)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => startRun(runDialog.report.key, runDialog.params)}
                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
              >
                Run
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Run detail ── */}
      <Dialog open={!!runDetail} onClose={() => setRunDetail(null)} maxWidth="md" fullWidth>
        {runDetail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {runDetail.reportName}
              <Chip size="small" color={STATUS_COLOR[runDetail.status] || 'default'} label={runDetail.status} />
            </DialogTitle>
            <DialogContent>
              {runDetail.status === 'running' && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress
                    variant={runDetail.total > 0 ? 'determinate' : 'indeterminate'}
                    value={runDetail.total > 0 ? (runDetail.processed / runDetail.total) * 100 : 0}
                  />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {runDetail.step}{runDetail.total > 0 ? ` ${runDetail.processed}/${runDetail.total}` : ''}
                  </Typography>
                </Box>
              )}
              <Kv rows={[
                ['Run id', runDetail.id],
                ['Trigger', `${runDetail.trigger}${runDetail.scheduleName ? ` · ${runDetail.scheduleName}` : ''}`],
                ['Started', runDetail.startedUtc ? new Date(runDetail.startedUtc).toLocaleString() : '—'],
                ['Duration', fmtDuration(runDetail.durationMs)],
                ...(runDetail.rowCount != null ? [['Rows', runDetail.rowCount.toLocaleString()]] : []),
                ...(runDetail.fileName ? [['File', `${runDetail.fileName} (${fmtBytes(runDetail.fileSize)})`]] : []),
              ]} />
              {runDetail.error && <Alert severity="error" sx={{ mt: 2 }}>{runDetail.error}</Alert>}

              {Object.keys(runDetail.parameters || {}).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Parameters</Typography>
                  <Kv rows={Object.entries(runDetail.parameters)} />
                </>
              )}
              {runDetail.summary && typeof runDetail.summary === 'object' && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Summary</Typography>
                  <Kv rows={Object.entries(runDetail.summary)
                    .filter(([k]) => k !== 'parameters')
                    .map(([k, v]) => [label(k), typeof v === 'object' ? JSON.stringify(v) : String(v)])} />
                </>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Log</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0, p: 1.5, maxHeight: 320, overflow: 'auto', fontSize: 12, borderRadius: 1,
                  bgcolor: alpha(theme.palette.text.primary, 0.04), border: `1px solid ${theme.palette.divider}`,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
              >
                {(runDetail.log || []).join('\n') || '—'}
              </Box>
            </DialogContent>
            <DialogActions>
              {runDetail.download && (
                <Button startIcon={<DownloadIcon />} onClick={() => download(runDetail)}>Download</Button>
              )}
              <Button
                startIcon={<ReplayIcon />}
                onClick={async () => {
                  const d = await startRun(runDetail.reportKey, runDetail.parameters || {});
                  if (d && d.id) loadRunDetail(d.id);
                }}
              >
                Run again
              </Button>
              {runDetail.status !== 'running' && runDetail.status !== 'queued' && emailOn && (
                <Button startIcon={<MailOutlineIcon />} onClick={() => emailRun(runDetail.id)}>Email this result</Button>
              )}
              {runDetail.status === 'running' || runDetail.status === 'queued' ? (
                <Button color="error" startIcon={<StopIcon />} onClick={() => cancelOrDelete(runDetail.id)}>Cancel</Button>
              ) : (
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => cancelOrDelete(runDetail.id)}>Delete record</Button>
              )}
              <Button onClick={() => setRunDetail(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ReportBuilderDialog
        open={!!builder}
        report={builder ? builder.report : null}
        onClose={() => setBuilder(null)}
        onSaved={() => { setBuilder(null); loadAll(); }}
        notify={showNotification}
      />

      <ParameterDefaultsDialog
        open={!!paramsFor}
        reportKey={paramsFor}
        onClose={() => setParamsFor(null)}
        onSaved={loadAll}
        notify={showNotification}
      />

      {/* ── Schedule dialog ── */}
      <Dialog open={!!schedDialog} onClose={() => setSchedDialog(null)} maxWidth="sm" fullWidth>
        {schedDialog && (
          <>
            <DialogTitle>{schedDialog.schedule ? 'Edit schedule' : 'New schedule'}</DialogTitle>
            <DialogContent>
              <TextField
                label="Name" value={schedDialog.form.name} size="small" fullWidth sx={{ mt: 1, mb: 2 }}
                onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, name: e.target.value } })}
              />
              <TextField
                select label="Report" value={schedDialog.form.reportKey} size="small" fullWidth sx={{ mb: 2 }}
                onChange={(e) => setSchedDialog({
                  ...schedDialog,
                  form: { ...schedDialog.form, reportKey: e.target.value, parameters: {} },
                })}
              >
                {reports.map((r) => <MenuItem key={r.key} value={r.key}>{r.name}</MenuItem>)}
              </TextField>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Cron (min hour dom mon dow)" value={schedDialog.form.cron}
                    size="small" fullWidth
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, cron: e.target.value } })}
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select label="Time zone" value={schedDialog.form.timeZone} size="small" fullWidth
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, timeZone: e.target.value } })}
                  >
                    {timezones.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              {cronPreview.length > 0 && (
                <Alert severity="success" icon={<ScheduleIcon fontSize="small" />} sx={{ mb: 2 }}>
                  <Typography variant="caption" component="div">
                    Next: {cronPreview.slice(0, 3).map((o) => new Date(o).toLocaleString()).join(' · ')}
                  </Typography>
                </Alert>
              )}

              {dialogReport && (dialogReport.parameters || []).map((p) => (
                <ParamField
                  key={p.name} p={p}
                  value={schedDialog.form.parameters[p.name] ?? (p.default ?? '')}
                  onChange={(v) => setSchedDialog({
                    ...schedDialog,
                    form: { ...schedDialog.form, parameters: { ...schedDialog.form.parameters, [p.name]: v } },
                  })}
                />
              ))}

              <FormControlLabel
                control={(
                  <Switch
                    checked={schedDialog.form.enabled}
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, enabled: e.target.checked } })}
                  />
                )}
                label="Enabled"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Email</Typography>
              {!emailOn && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="caption">
                    Notifications are switched off site-wide, so nothing here will send until they are
                    turned on from the Email tab.
                  </Typography>
                </Alert>
              )}
              <FormControlLabel
                control={(
                  <Switch
                    checked={schedDialog.form.emailEnabled !== false}
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, emailEnabled: e.target.checked } })}
                  />
                )}
                label="Notify when this schedule runs"
              />
              {schedDialog.form.emailEnabled !== false && (
                <>
                  <TextField
                    label="Extra recipients (To)" size="small" fullWidth sx={{ mt: 1 }}
                    value={schedDialog.form.emailTo || ''}
                    placeholder={(email && email.settings && email.settings.defaultTo) || 'someone@safavieh.com'}
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, emailTo: e.target.value } })}
                  />
                  <TextField
                    label="Extra Cc" size="small" fullWidth sx={{ mt: 2 }}
                    value={schedDialog.form.emailCc || ''}
                    onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, emailCc: e.target.value } })}
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!!schedDialog.form.emailRecipientsReplaceDefault}
                        onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, emailRecipientsReplaceDefault: e.target.checked } })}
                      />
                    )}
                    label="Send only to these, not the site-wide defaults"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={schedDialog.form.emailAttachFile !== false}
                        onChange={(e) => setSchedDialog({ ...schedDialog, form: { ...schedDialog.form, emailAttachFile: e.target.checked } })}
                      />
                    )}
                    label="Attach the output file"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSchedDialog(null)}>Cancel</Button>
              <Button
                variant="contained" onClick={saveSchedule}
                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ReportsScheduler;
